/* global THREE */
import HopeCategoryStone from '@/atoms/HopeCategoryStone'
import HopeStoneTexture from '@/atoms/HopeStoneTexture'
import HopeTotemLabel from '@/atoms/HopeTotemLabel'
import find from 'lodash/find'
import applyToonLighting from '@/utils/applyToonLighting'

export default class HopeTotem extends THREE.Group {
  // constructor
  constructor (config, isUserTotem = false, userTotem) {
    super()
    this._isUserTotem = isUserTotem
    this._stones = []
    this._stoneShadows = []
    this._debug = false
    this._rotate = true
    this._oscillate = true
    this._collisionLOD = 0
    this._id = config.id
    this._spacing = 0
    this._allStonesExploded = false

    // this.position.x = config.position.x
    // this.position.z = config.position.z
    // this.position.y = 0
    this.doUpdate = true

    this.config = config
    this.label = new HopeTotemLabel(config.label)

    this._distance = 0

    let deg2rad = (d) => d * (Math.PI / 180)

    if (this._isUserTotem) {
      this.label.draw(this._config.label, 'Your hopes, by topic')
    } else {
      if (userTotem) {
        let currentDistance = this.config.stones[0].concepts[0].latlon
        let userDistance = {
          lat: userTotem[0]._config.geolocation.lat,
          lon: userTotem[0]._config.geolocation.lng
        }

        let R = 6371e3

        let lat1 = deg2rad(parseFloat(currentDistance.split(',')[0]))
        let lon1 = deg2rad(parseFloat(currentDistance.split(',')[1]))

        let lat2 = deg2rad(userDistance.lat)
        let lon2 = deg2rad(userDistance.lon)

        let phi1 = lat1
        let phi2 = lat2
        let delta1 = lat2 - lat1
        let delta2 = lon2 - lon1

        let a = Math.sin(delta1 / 2) * Math.sin(delta1 / 2) + Math.cos(phi1) * Math.cos(phi2) * Math.sin(delta2 / 2) * Math.sin(delta2 / 2)
        let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

        this._distance = Math.round(R * c / 1000 * 0.621371)

        this.label.draw(this._config.label, this._distance + ' Miles')
      }
    }

    this.add(this.label)
  }

  get allStonesExploded () {
    return this._allStonesExploded
  }

  set allStonesExploded (value) {
    if (value !== this._allStonesExploded) {
      this._allStonesExploded = value

      if (value) {
        if (this._isUserTotem) {
          this.label.draw(this._config.label, 'All your hopes')
        } else {
          this.label.draw(this._config.label, this._distance + ' Miles')
        }
      } else {
        if (this._isUserTotem) {
          this.label.draw(this._config.label, 'Your hopes, by topic')
        } else {
          this.label.draw(this._config.label, this._distance + ' Miles')
        }
      }
    }
  }

  set spacing (value) {
    this._spacing = value
    for (let i = this._stones.length - 1; i >= 0; i--) {
      this._stones[i].spacing = value
    }
  }

  get spacing () {
    return this._spacing
  }

  findCategoryStoneByConcept (concept) {
    let ret = null
    this._stones.some((stone) => {
      if (stone.hasConcept(concept)) {
        ret = stone
        return true
      }
      return false
    })
    return ret
  }

  destroy () {
    this._stones.forEach(stone => {
      stone.destroy()
      this.remove(stone)
    })
    this._stones = []
  }
  get stones () {
    return this._stones
  }

  // handle variation (update, remove, add) on stones
  set config (value) {
    // remove stones
    // inverse loop for removing
    for (let i = this._stones.length - 1; i >= 0; i--) {
      let stone = this._stones[i]
      if (!find(value.stones, {id: stone.config.id})) {
        stone.destroy()
        this.remove(stone)
        this._stones.splice(i, 1)

        let shadow = this._stoneShadows[i]
        shadow.material.map.dispose()
        shadow.material.dispose()
        shadow.geometry.dispose()
        this.remove(shadow)
        this._stoneShadows.splice(i, 1)
      }
    }

    // add/update stones
    value.stones.forEach((stoneConfig, i) => {
      let existingStone = this.getStone(stoneConfig)
      if (!existingStone) {
        let material = new THREE.MeshToonMaterial({
          specular: new THREE.Color(0x000000),
          shininess: 0,
          transparent: true,
          opacity: 0,
          map: new HopeStoneTexture(stoneConfig.color, stoneConfig.concepts.length)
        })
        material.onBeforeCompile = function (mat) {
          mat.fragmentShader = mat.fragmentShader.replace('#include <fog_fragment>', '#include <fog_fragment>;\n#ifdef USE_FOG\ngl_FragColor.a *= 2.0 - fogFactor;\n#endif')
          applyToonLighting(mat)
        }
        let stone = new HopeCategoryStone(stoneConfig, material)
        // stone.elasticPosition.y = stone.position.y = this._stones[this._stones.length - 1].position.y + 1.5
        if (i > 0) {
          let prevStone = this._stones[i - 1]
          this._initStoneY(stone, prevStone)
        }
        stone.debug = this._debug
        stone.rotate = this._rotate
        stone.oscillate = this._oscillate
        stone.activeCollisionLOD = this._collisionLOD
        this.add(stone)
        this._stones.push(stone)

        let geom = new THREE.PlaneBufferGeometry(2, 2)
        let shadowMat = new THREE.MeshBasicMaterial({
          transparent: true,
          opacity: 0.15,
          map: new THREE.TextureLoader().load('/static/img/shadow1.png'),
          // side: THREE.DoubleSide,
          // depthTest: false,
          depthWrite: false
        })
        let shadow = new THREE.Mesh(geom, shadowMat)
        shadow.rotation.x = -(Math.PI / 2)
        shadow.position.y = -0.48
        shadow.position.z = -(i * 0.5)
        shadow.position.x = -(i * 0.5)
        this.add(shadow)
        this._stoneShadows.push(shadow)
      } else {
        existingStone.config = stoneConfig
        if (i > 0) {
          let prevStone = this._stones[i - 1]
          this._initStoneY(existingStone, prevStone)
        }
      }
    })

    this._config = value
  }

  _initStoneY (stone, prevStone) {
    let thisStoneRadius = stone.unexplodedStone.collisionMesh.geometry.boundingSphere.radius * 0.9
    let thisStoneScale = 0.3 + (stone.config.concepts.length / 4)
    let prevStoneRadius = prevStone.unexplodedStone.collisionMesh.geometry.boundingSphere.radius * 0.9
    let prevStoneScale = 0.3 + (prevStone.config.concepts.length / 4)
    let prevStoneY = prevStone.getY()
    stone.setY(prevStoneY + (prevStoneRadius * prevStoneScale) + (thisStoneRadius * thisStoneScale))
  }

  getStone (stoneData) {
    return find(this._stones, {config: {id: stoneData.id}})
  }

  get config () {
    return this._config
  }

  get id () {
    return this._id
  }

  get collisionLOD () {
    return this._collisionLOD
  }

  set collisionLOD (value) {
    this._collisionLOD = value

    for (let i = 0; i < this._stones.length; i++) {
      this._stones[i].activeCollisionLOD = value
    }
  }
  get debug () {
    return this._debug
  }

  set debug (value) {
    this._debug = value

    for (let i = 0; i < this._stones.length; i++) {
      this._stones[i].debug = value
    }
  }

  get rotate () {
    return this._rotate
  }

  set rotate (value) {
    this._rotate = value

    for (let i = 0; i < this._stones.length; i++) {
      this._stones[i].rotate = value
    }
  }

  get oscillate () {
    return this._oscillate
  }

  set oscillate (value) {
    this._oscillate = value

    for (let i = 0; i < this._stones.length; i++) {
      this._stones[i].oscillate = value
    }
  }

  update (delta, ground) {
    if (this.doUpdate) {
      for (let i = 0; i < this._stones.length; i++) {
        if (i > 0) {
          this._stones[i].update(this._stones[i - 1].upperCollider, delta)
        } else {
          this._stones[i].update(ground, delta)
        }

        let shadow = this._stoneShadows[i]
        let y = this._stones[i].unexplodedStone.position.y
        shadow.position.z = -0.2 - (y * 0.8)
        shadow.position.x = -0.2 - (y * 0.8)
      }

      if (this._stones.length) {
        let top = this._stones[this._stones.length - 1].upperCollider
        this.label.position.y = top.position.y + (top.collisionMesh.geometry.boundingSphere.radius * top.scale.y)
        this.label.visible = this.doUpdate
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/atoms/HopeTotem.js